import { TQRCodeData } from "../hooks/useTQRCodeData"
import { TQR_CODE_CONFIG } from "./config"

/**
 * Extracts the UUID from QR code URL returned by the API
 */
export const extractUUIDFromQRCodeURL = (qrCodeUrl: string): string | null => {
  try {
    const url = new URL(qrCodeUrl)
    const dataParam = url.searchParams.get("data")

    return dataParam
  } catch {
    return null
  }
}

/**
 * Get the expiration time for a QR code
 */
export const getExpirationTime = (baseTime: number) => (
  Math.floor(baseTime / 1000)
    + TQR_CODE_CONFIG.DURATION
    + TQR_CODE_CONFIG.TIME_TOLERANCE
)

/**
 * Splits a string into roughly equal chunks
 */
const splitIntoChunks = (str: string, numChunks: number): string[] => {
  const chunkSize = Math.ceil(str.length / numChunks)
  const chunks: string[] = []

  for (let i = 0; i < str.length; i += chunkSize) {
    chunks.push(str.slice(i, i + chunkSize))
  }

  return chunks
}

/**
 * Formats a single chunk with metadata for TQR code generation
 * Format: {part_number}^{total_parts}^{code_id}^{data_chunk}
 */
const formatChunk = (
  partNumber: number,
  totalParts: number,
  guestId: string,
  chunk: string,
): string => {
  const formattedChunk = `${partNumber}^${totalParts}^${guestId}^${chunk}`

  return formattedChunk
}

/**
 * Creates formatted chunks from TQR code data
 * Format for each chunk: {part_number}^{total_parts}^{code_id}^{data_chunk}
 */
export const createQRCodeChunks = (
  tqrCodeData: TQRCodeData,
  guestId: string,
  numberOfChunks: number = 3,
): string[] => {
  const jsonString = JSON.stringify(tqrCodeData)
  const chunks = splitIntoChunks(jsonString, numberOfChunks)

  return chunks.map((chunk, index) => formatChunk(index + 1, numberOfChunks, guestId, chunk))
}

/**
 * Attempts to fetch normalized server time, with a fallback to local time
 */
export const getCurrentTimeWithFallback = async (): Promise<number> => {
  if (!navigator.onLine) {
    return Date.now()
  }

  try {
    const response = await fetch("/api/server-time")
    const data = await response.json()

    return data.serverTime
  } catch {
    return Date.now()
  }
}

/**
 * Creates a QRCodeData object with a computed expiration
 */
export const createTQRCodeData = (
  uuid: string,
  currentTime: number,
): TQRCodeData => ({
  id: uuid,
  exp: getExpirationTime(currentTime),
})

/**
 * Calculates the elapsed and remaining time for a TQR code
 */
export const getTQRCodeElapsedAndRemaining = (
  startTime: number,
  duration: number,
): { elapsed: number; remaining: number } => {
  const now = Date.now()
  const elapsed = Math.floor((now - startTime) / 1000)
  const remaining = Math.max(duration - elapsed, 0)

  return { elapsed, remaining }
}
